import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import SanitizedHtml from '../SanitizedHtml.jsx';
import {
  ASSESSMENT_COLORS,
  ASSESSMENT_STATUS_META,
  getAssessmentColor,
  getAssessmentLabel,
  getAssessmentProvenanceLabel,
  normalizeAssessmentColor,
  normalizeAssessments,
} from '../../utils/assessmentPresentation.js';

const CONTEXT_LABELS = {
  population: 'População',
  plantPart: 'Parte utilizada',
  preparation: 'Preparação',
  dose: 'Dose',
  route: 'Via',
  comparator: 'Comparador',
  outcome: 'Desfecho',
  followUp: 'Acompanhamento',
  mapCode: 'Código do mapa',
  mapTitle: 'Título do mapa',
  mapPageUrl: 'Página do mapa',
  studyId: 'ID no mapa',
  interventionAsReported: 'Espécie/intervenção reportada no mapa',
  interventionDetailsStatus: 'Detalhamento da intervenção',
  outcomeGroup: 'Grupo de desfechos (fonte)',
  reviewType: 'Tipo de revisão (fonte)',
  rawEffect: 'Direção original (fonte)',
  rawConfidence: 'Confiança original (fonte)',
  assessmentOrigin: 'Origem da avaliação',
  reportedBy: 'Reportado por',
  analysisState: 'Estado da análise clínica',
  requiresFullTextAssessment: 'Requer texto integral',
  crosswalkVersion: 'Versão do mapeamento',
};

const CONTEXT_VALUE_LABELS = {
  REPORTED_BY_SOURCE: 'Reportado pela fonte',
  INDEPENDENT_ASSESSMENT: 'Avaliação independente',
  PENDING_CLAIM_ANALYSIS: 'Análise clínica pendente',
  NOT_REPORTED: 'Não informado',
  PARTIAL: 'Parcial',
  COMPLETE: 'Completo',
};

const ITEM_TYPE_LABELS = {
  metabolite: 'Relação espécie–metabólito',
  therapeuticEffect: 'Relação espécie–efeito terapêutico',
};

const formatContextKey = (key) => CONTEXT_LABELS[key]
  || key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (letter) => letter.toUpperCase());

const formatContextValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  if (CONTEXT_VALUE_LABELS[value]) return CONTEXT_VALUE_LABELS[value];
  return String(value);
};

/**
 * Exibe todas as avaliações, fontes e contexto de uma associação.
 *
 * @param {object} props - Dados do relacionamento selecionado
 * @returns {JSX.Element|null} Modal do Painel de Evidência
 */
const EvidencePanelModal = ({
  isOpen,
  onClose,
  specieName = '',
  relationName,
  relationDescription = '',
  itemType,
  assessments = [],
  relevance = null,
  relationshipComment = '',
}) => {
  const titleId = useId();
  const closeButtonRef = useRef(null);
  const normalizedAssessments = normalizeAssessments(assessments, relevance, itemType);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousActiveElement = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousActiveElement?.focus?.();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/55 p-3 sm:p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-greenbook-dark-bg dark:text-greenbook-dark-text"
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4 dark:border-gray-700 sm:px-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-greenbook-light-primary dark:text-greenbook-dark-primary">
              Painel de Evidência
            </p>
            <h2 id={titleId} className="my-1 text-xl sm:text-2xl">
              {relationName}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {specieName} · {ITEM_TYPE_LABELS[itemType]}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Fechar Painel de Evidência"
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-semibold hover:border-greenbook-light-primary dark:border-gray-600 dark:hover:border-greenbook-dark-primary"
          >
            Fechar
          </button>
        </header>

        <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5 sm:px-6">
          {relationDescription && (
            <SanitizedHtml
              html={relationDescription}
              className="text-sm leading-relaxed text-gray-700 dark:text-gray-200 [&_p]:my-1"
            />
          )}

          {relationshipComment && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-900/25">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Observação da relação
              </p>
              <p className="mt-1 whitespace-pre-wrap">{relationshipComment}</p>
            </div>
          )}

          <div className="space-y-3">
            <h3 className="font-montserrat text-lg font-semibold">Avaliações</h3>

            {normalizedAssessments.length > 0 ? normalizedAssessments.map((assessment) => {
              const context = assessment.context || {};
              const contextEntries = Object.entries(context).filter(([key]) => (
                key !== 'intervention'
                || !context.interventionAsReported
              ));
              const statusDescription = ASSESSMENT_STATUS_META[assessment.status]?.description;
              const assessmentDescription = assessment.status === 'ASSESSED'
                ? assessment.value?.description
                : statusDescription;
              const provenanceLabel = getAssessmentProvenanceLabel(assessment);
              const isPendingClaimAnalysis = (
                context.analysisState === 'PENDING_CLAIM_ANALYSIS'
              );

              return (
                <article
                  key={assessment.id || `${assessment.dimension?.code}:${assessment.value?.code}`}
                  className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className="mt-1 h-4 w-4 flex-shrink-0 rounded-full border border-black/10 dark:border-white/20"
                      style={{ backgroundColor: getAssessmentColor(assessment) }}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div>
                          <p className="font-semibold">
                            {assessment.dimension?.name || 'Avaliação'}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-300">
                            {getAssessmentLabel(assessment)}
                          </p>
                        </div>
                        {assessment.dimension?.framework?.name && (
                          <span
                            className="rounded-full border px-2.5 py-1 text-xs font-semibold"
                            style={{
                              borderColor: normalizeAssessmentColor(
                                assessment.dimension.framework.hexColor,
                              ),
                            }}
                          >
                            {assessment.dimension.framework.name}
                          </span>
                        )}
                      </div>

                      {assessmentDescription && (
                        <SanitizedHtml
                          html={assessmentDescription}
                          className="mt-3 text-sm leading-relaxed text-gray-700 dark:text-gray-200 [&_p]:my-1"
                        />
                      )}

                      {provenanceLabel && (
                        <p className="mt-3 text-xs font-semibold text-gray-600 dark:text-gray-300">
                          {provenanceLabel}
                        </p>
                      )}

                      {isPendingClaimAnalysis && (
                        <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/25 dark:text-amber-100">
                          O mapa identifica a espécie, mas não detalha aqui a preparação,
                          dose, via e duração usadas nos estudos. Esses campos aguardam
                          leitura e avaliação do texto integral.
                        </div>
                      )}

                      {assessment.comment && assessment.comment !== relationshipComment && (
                        <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-200">
                          {assessment.comment}
                        </p>
                      )}

                      {contextEntries.length > 0 && (
                        <dl className="mt-4 grid gap-3 border-t border-gray-200 pt-3 sm:grid-cols-2 dark:border-gray-700">
                          {contextEntries.map(([key, value]) => (
                            <div key={key}>
                              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {formatContextKey(key)}
                              </dt>
                              <dd className="mt-0.5 break-words text-sm">
                                {key.toLowerCase().endsWith('url') ? (
                                  <a
                                    href={String(value)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-greenbook-light-primary underline underline-offset-2 hover:no-underline dark:text-greenbook-dark-primary"
                                  >
                                    {formatContextValue(value)}
                                  </a>
                                ) : formatContextValue(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}

                      {assessment.sourceDocument && (
                        <div className="mt-4 border-t border-gray-200 pt-3 text-sm dark:border-gray-700">
                          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                            Fonte
                          </p>
                          <p className="mt-1">
                            {assessment.sourceDocument.sourceUrl ? (
                              <a
                                href={assessment.sourceDocument.sourceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-greenbook-light-primary underline underline-offset-2 hover:no-underline dark:text-greenbook-dark-primary"
                              >
                                {assessment.sourceDocument.title}
                              </a>
                            ) : assessment.sourceDocument.title}
                            {assessment.sourceDocument.publicationYear
                              ? ` (${assessment.sourceDocument.publicationYear})`
                              : ''}
                          </p>
                          {assessment.sourceDocument.publisher && (
                            <p className="text-xs text-gray-600 dark:text-gray-300">
                              {assessment.sourceDocument.publisher}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            }) : (
              <div className="flex items-center gap-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
                <span
                  className="h-4 w-4 rounded-full border border-black/10 dark:border-white/20"
                  style={{
                    backgroundColor: normalizeAssessmentColor(ASSESSMENT_COLORS.UNKNOWN),
                  }}
                  aria-hidden="true"
                />
                <div>
                  <p className="font-semibold">Não avaliado</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Ainda não há uma avaliação cadastrada para esta associação.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>,
    document.body,
  );
};

EvidencePanelModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  specieName: PropTypes.string,
  relationName: PropTypes.string.isRequired,
  relationDescription: PropTypes.string,
  itemType: PropTypes.oneOf(['metabolite', 'therapeuticEffect']).isRequired,
  assessments: PropTypes.arrayOf(PropTypes.object),
  relevance: PropTypes.object,
  relationshipComment: PropTypes.string,
};

export default EvidencePanelModal;
